import { useRef, useEffect, useState, useMemo } from 'react';
import './Navbar.css';

const Navbar = ({
  animationTime = 600,
  particleCount = 35,
  particleDistances = [90, 10],
  particleR = 300,
  timeVariance = 400,
  colors = [1, 1, 2, 1, 1, 2, 1, 1, 2, 1, 2, 1, 2],
  initialActiveIndex = 0
}) => {
  const containerRef = useRef(null);
  const navRef = useRef(null);
  const filterRef = useRef(null);
  const textRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(initialActiveIndex);

  const navItems = useMemo(() => [
    { id: 'hero', label: 'Accueil', href: '#hero' },
    { id: 'equipe', label: "L'Équipe", href: '#equipe' },
    { id: 'reseaux', label: 'Réseaux', href: '#reseaux' },
    { id: 'partenaires', label: 'Partenaires', href: '#partenaires' },
    { id: 'episodes', label: 'Épisodes', href: '#episodes' }
  ], []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const noise = (n = 1) => n / 2 - Math.random() * n;

  const getXY = (distance, pointIndex, totalPoints) => {
    const angle = ((360 + noise(8)) / totalPoints) * pointIndex * (Math.PI / 180);
    return [distance * Math.cos(angle), distance * Math.sin(angle)];
  };

  const createParticle = (i, t, d, r) => {
    let rotate = noise(r / 10);
    return {
      start: getXY(d[0], particleCount - i, particleCount),
      end: getXY(d[1] + noise(7), particleCount - i, particleCount),
      time: t,
      scale: 1 + noise(0.2),
      color: colors[Math.floor(Math.random() * colors.length)],
      rotate: rotate > 0 ? (rotate + r / 20) * 10 : (rotate - r / 20) * 10
    };
  };

  const makeParticles = element => {
    const d = particleDistances;
    const r = particleR;
    const bubbleTime = animationTime * 2 + timeVariance;
    element.style.setProperty('--time', `${bubbleTime}ms`);

    // Add active class immediately
    element.classList.add('active');

    for (let i = 0; i < particleCount; i++) {
      const t = animationTime * 2 + noise(timeVariance * 2);
      const p = createParticle(i, t, d, r);
      
      // Create particles immediately without delay
      const particle = document.createElement('span');
      const point = document.createElement('span');
      particle.classList.add('particle');
      particle.style.setProperty('--start-x', `${p.start[0]}px`);
      particle.style.setProperty('--start-y', `${p.start[1]}px`);
      particle.style.setProperty('--end-x', `${p.end[0]}px`);
      particle.style.setProperty('--end-y', `${p.end[1]}px`);
      particle.style.setProperty('--time', `${p.time}ms`);
      particle.style.setProperty('--scale', `${p.scale}`);
      particle.style.setProperty('--color', `var(--color-${p.color}, white)`);
      particle.style.setProperty('--rotate', `${p.rotate}deg`);
      point.classList.add('point');
      particle.appendChild(point);
      element.appendChild(particle);
      
      setTimeout(() => {
        try {
          element.removeChild(particle);
        } catch {
          // Do nothing
        }
      }, t);
    }
  };

  const updateEffectPosition = element => {
    if (!containerRef.current || !filterRef.current || !textRef.current) return;
    const containerRect = containerRef.current.getBoundingClientRect();
    const pos = element.getBoundingClientRect();
    const styles = {
      left: `${pos.x - containerRect.x}px`,
      top: `${pos.y - containerRect.y}px`,
      width: `${pos.width}px`,
      height: `${pos.height}px`
    };
    Object.assign(filterRef.current.style, styles);
    Object.assign(textRef.current.style, styles);
    textRef.current.innerText = element.innerText;
  };

  const handleClick = (e, index) => {
    e.preventDefault();
    const liEl = e.currentTarget.parentElement;
    if (activeIndex === index) return;
    
    // Update position FIRST before creating particles
    updateEffectPosition(liEl);
    
    // Remove old particles immediately
    if (filterRef.current) {
      const particles = filterRef.current.querySelectorAll('.particle');
      particles.forEach(p => filterRef.current.removeChild(p));
    }
    
    // Update active state
    setActiveIndex(index);
    
    // Update text effect
    if (textRef.current) {
      textRef.current.classList.remove('active');
      void textRef.current.offsetWidth;
      textRef.current.classList.add('active');
    }
    
    // Create particles immediately at the new position
    if (filterRef.current) {
      // Use requestAnimationFrame to ensure position is updated
      requestAnimationFrame(() => {
        makeParticles(filterRef.current);
      });
    }
    
    // Scroll after particles are positioned
    scrollToSection(navItems[index].id);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const liEl = e.currentTarget.parentElement;
      if (liEl) {
        handleClick(e, index);
      }
    }
  };

  // Scroll spy disabled - links only activate on click
  useEffect(() => {
    // Empty effect - scroll spy functionality removed
  }, [navItems]);

  useEffect(() => {
    if (!navRef.current || !containerRef.current) return;
    const activeLi = navRef.current.querySelectorAll('li')[activeIndex];
    if (activeLi) {
      updateEffectPosition(activeLi);
      textRef.current?.classList.add('active');
    }

    const resizeObserver = new ResizeObserver(() => {
      const currentActiveLi = navRef.current?.querySelectorAll('li')[activeIndex];
      if (currentActiveLi) {
        updateEffectPosition(currentActiveLi);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [activeIndex]);

  return (
    <div className="gooey-nav">
      <div className="gooey-nav-container" ref={containerRef}>
        <nav>
          <ul ref={navRef}>
            {navItems.map((item, index) => (
              <li key={index} className={activeIndex === index ? 'active' : ''}>
                <a 
                  href={item.href} 
                  onClick={e => handleClick(e, index)} 
                  onKeyDown={e => handleKeyDown(e, index)}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
        <span className="effect filter" ref={filterRef} />
        <span className="effect text" ref={textRef} />
      </div>
    </div>
  );
};

export default Navbar;
